/**
 * The post-consultation CI queue — membership and the consult-note reads.
 *
 * The tab's job is turning a completed consult into a payment, so the columns
 * that carry the NP's verdict — next step, quick note — are live
 * `QuestionnaireResponse`s from the **APP - Consultation H&P** PlanDefinition.
 * This list reads them rather than introducing a form. A PN consultation has no
 * H&P; its next step is the Outcome on the `pn-consultation-note` card, read
 * through `pac-call-notes.ts`.
 *
 * Membership derives from the consult appointment, not from
 * `bariatric-procedure-stage`, for the same two reasons as the pre-consultation
 * tab: the tag drifts, and resolving a cohort from it does not scale.
 */

import { Coding, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { appointmentHasOccurred, isVoidAppointment, ListAppointment } from './pac-list-appointments';
import { getClinicalTaskDueDate, isClinicalTaskOverdue } from './clinical-tasks';

/** Canonical URLs of the three consult-note questionnaires. */
export const NEXT_STEP_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/next-step';
export const WARM_TRANSFER_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/warm-transfer-outcome';
export const QUICK_NOTE_PN_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/quick-note-pn';

export const CONSULT_NOTE_QUESTIONNAIRE_URLS = [
  NEXT_STEP_QUESTIONNAIRE_URL,
  WARM_TRANSFER_QUESTIONNAIRE_URL,
  QUICK_NOTE_PN_QUESTIONNAIRE_URL,
] as const;

/**
 * What the post-consultation list actually fetches. Warm transfer left the list
 * (BAR-2591) — the hand-off is the PN consultation itself under Intake 1.1 — so
 * the tab no longer pays for that response. The three-URL constant above stays
 * as the access-policy contract.
 */
export const POST_CONSULTATION_LIST_QUESTIONNAIRE_URLS = [
  NEXT_STEP_QUESTIONNAIRE_URL,
  QUICK_NOTE_PN_QUESTIONNAIRE_URL,
] as const;

/** `linkId` equals the questionnaire `name` for all three. */
const NEXT_STEP_LINK_ID = 'next-step';
const QUICK_NOTE_LINK_ID = 'quick-note-pn';

export const NEXT_STEP_CLEARED_TO_SCHEDULE = 'cleared-to-schedule';
export const NEXT_STEP_NO_LONGER_INTERESTED = 'no-longer-interested';

/** Days of silence before the "No contact" chip matches. */
export const NO_CONTACT_CHIP_DAYS = 7;

/**
 * Membership: the consult happened, or the patient no-showed it. The Intake
 * hub passes `includeNoShows: false`, since its Consultation no-show tab owns
 * missed consults (BAR-2809, `pac-no-show-list.ts`); the legacy tab keeps them.
 *
 * A patient whose procedure has already happened IS excluded — CI has nothing
 * left to sell them. Payment, not the procedure, is the precise boundary, but
 * payment state is not on this query and the procedure is.
 */
export function isPostConsultationLead(
  lead: { consult?: ListAppointment; procedure?: ListAppointment },
  now: Date,
  { includeNoShows = true }: { includeNoShows?: boolean } = {}
): boolean {
  // `consult` is the patient's LATEST live consult, APP or PN — see
  // `latestConsultPerPatient`. Membership is judged on that one alone.
  if (!lead.consult) {
    return false;
  }

  const attended = appointmentHasOccurred(lead.consult, now);
  const noShowed = includeNoShows && lead.consult.status === 'noshow';

  if (!attended && !noShowed) {
    return false;
  }

  return !(lead.procedure && appointmentHasOccurred(lead.procedure, now));
}

/**
 * The soonest open clinical task with a due date.
 *
 * Undated open tasks are skipped rather than sorted last: the column's whole
 * job is "what am I late on", and a task with no due date cannot answer it. The
 * cell renders an em-dash instead.
 */
export function getNextTask(tasks: Task[] | undefined): Task | undefined {
  return (tasks ?? [])
    .filter((task) => getClinicalTaskDueDate(task) !== undefined)
    .sort((a, b) => (getClinicalTaskDueDate(a) ?? '').localeCompare(getClinicalTaskDueDate(b) ?? ''))[0];
}

/** Any open task whose due date has passed. */
export function hasOverdueTask(tasks: Task[] | undefined, now: Date): boolean {
  return (tasks ?? []).some((task) => isClinicalTaskOverdue(task, now));
}

function answersFor(responses: QuestionnaireResponse[] | undefined, url: string, linkId: string) {
  // Newest first: the caller sorts by `-authored`, and a re-signed note supersedes
  // an earlier one rather than merging with it.
  const response = responses?.find((entry) => entry.questionnaire === url);
  return response?.item?.find((item) => item.linkId === linkId)?.answer ?? [];
}

/**
 * `next-step` is `repeats: true` — an NP may record more than one.
 *
 * Returned whole rather than pre-flattened so the cell can render "first + `+N`"
 * while the chip still tests every value. Flattening to a display string here
 * would make `isClearedToSchedule` a substring match, which
 * `not-cleared-further-steps-needed` would satisfy.
 */
export function getNextSteps(responses?: QuestionnaireResponse[]): Coding[] {
  return answersFor(responses, NEXT_STEP_QUESTIONNAIRE_URL, NEXT_STEP_LINK_ID)
    .map((answer) => answer.valueCoding)
    .filter((coding): coding is Coding => coding !== undefined);
}

export function getQuickNote(responses?: QuestionnaireResponse[]): string | undefined {
  const value = answersFor(responses, QUICK_NOTE_PN_QUESTIONNAIRE_URL, QUICK_NOTE_LINK_ID)[0]?.valueString;
  return value && value.trim().length > 0 ? value : undefined;
}

export function isClearedToSchedule(nextSteps: Coding[]): boolean {
  return nextSteps.some((coding) => coding.code === NEXT_STEP_CLEARED_TO_SCHEDULE);
}

/**
 * Silence for a week or more.
 *
 * A lead nobody has ever contacted counts as no-contact — the chip is asking
 * "who is going quiet", and never-contacted is the loudest possible answer.
 */
export function isNoContactSince(lastSent: string | undefined, now: Date, days = NO_CONTACT_CHIP_DAYS): boolean {
  if (!lastSent) {
    return true;
  }

  const elapsedMs = now.getTime() - new Date(lastSent).getTime();
  return elapsedMs >= days * 24 * 60 * 60 * 1000;
}

/**
 * "First answer + `+N`". Wrapping the cell would break the row rhythm across the
 * whole table for a minority of rows; the full set is on the patient page.
 */
export function formatNextStepCell(nextSteps: Coding[]): string {
  const [first, ...rest] = nextSteps;

  if (!first) {
    return '';
  }

  const label = first.display ?? first.code ?? '';
  return rest.length > 0 ? `${label} +${rest.length}` : label;
}

/**
 * The consult the `latest-consult` tag goes on: the patient's latest live
 * consult, while no procedure has happened (BAR-2865). Whether it has happened
 * yet is left to the queue's query, so the tag doesn't depend on the clock.
 */
export function queueConsult<T extends ListAppointment>(
  consults: T[],
  procedures: ListAppointment[],
  now: Date
): T | undefined {
  if (procedures.some((procedure) => appointmentHasOccurred(procedure, now))) {
    return undefined;
  }
  return consults
    .filter((consult) => !isVoidAppointment(consult))
    .sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''))[0];
}

/**
 * One consult per patient: the latest live one, whichever type it is.
 *
 * The queue query returns every APP and PN consult in range, newest first. A
 * patient who had an NP consult and then a PN consultation would otherwise be
 * two rows with two statuses and two next steps. The row is the patient, and
 * the consult that describes them is the most recent — a rebooked consult
 * supersedes the one it replaced, and its status and chart note are the ones
 * that say what happens next.
 *
 * Preserves the caller's order and takes the first seen per patient, so the
 * CALLER decides "latest" by handing this newest-first — never re-sorted here.
 * The query's `_sort: -date` alone is not enough once a list is live: a consult
 * merged in by subscription is appended, not sorted in (BAR-2708), so a caller
 * on a live list must sort before calling.
 */
export function latestConsultPerPatient<T>(consults: T[], patientRefOf: (consult: T) => string | undefined): T[] {
  const seen = new Set<string>();
  const latest: T[] = [];

  for (const consult of consults) {
    const patientRef = patientRefOf(consult);
    if (!patientRef || seen.has(patientRef)) {
      continue;
    }
    seen.add(patientRef);
    latest.push(consult);
  }

  return latest;
}
